import { html, css, svg } from 'lit';
import { QghBase } from './base.js';
import { isLive, fmtTime, translateCountry } from './utils.js';

const ROUNDS_DEF = [
  { api: 'Round of 32',     label: 'Dieciseisavos', short: 'R32', main: true  },
  { api: 'Round of 16',     label: 'Octavos',        short: 'R16', main: true  },
  { api: 'Quarter-finals',  label: 'Cuartos',        short: 'QF',  main: true  },
  { api: 'Semi-finals',     label: 'Semifinales',    short: 'SF',  main: true  },
  { api: '3rd Place Final', label: '3er Puesto',     short: '3°',  main: false },
  { api: 'Final',           label: 'Final',          short: 'F',   main: true  },
];

// Layout constants — CARD_H must exactly match the rendered card height.
// Card = 1px top border + ROW_H (row 1) + ROW_H (row 2, includes its border-top) + 1px bottom border
const ROW_H    = 40;              // px — each team row, border-box
const CARD_H   = ROW_H * 2 + 2;  // 82px — 2 rows + 2px outer card border
const CARD_W   = 152;             // px
const CON_W    = 28;              // px — connector strip between columns
const BASE_GAP = 20;              // px — gap between cards in the base round
const BASE_SLOT = CARD_H + BASE_GAP;

export class QghBracket extends QghBase {
  static properties = {
    ...QghBase.properties,
    title:        { type: String },
    theme:        { type: String },
    _activeRound: { state: true },
    _zoom:        { state: true },
  };

  static styles = [
    ...QghBase.styles,
    css`
      :host {
        display: block;
        font-family: 'Roboto', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        background: var(--qgh-bg);
        border-radius: var(--qgh-radius);
      }
      .widget { background: var(--qgh-bg); color: var(--qgh-color); border-radius: var(--qgh-radius); overflow: hidden; }

      /* ── Header ─────────────────────────────────────── */
      .header {
        display: flex; align-items: center; justify-content: space-between;
        padding: 10px 14px 0;
      }
      .header-title {
        font-size: 11px; font-weight: 700; letter-spacing: .08em;
        text-transform: uppercase; color: var(--qgh-accent);
      }
      .zoom-btns { display: flex; gap: 3px; }
      .zoom-btn {
        background: var(--qgh-nav-bg); border: none; cursor: pointer;
        color: var(--qgh-color); width: 22px; height: 22px; border-radius: 3px;
        font-size: 15px; font-weight: 700; line-height: 1;
        display: flex; align-items: center; justify-content: center;
        transition: background .15s;
      }
      .zoom-btn:hover { background: var(--qgh-nav-hover); }
      .zoom-btn:disabled { opacity: .28; cursor: default; }

      /* ── Round nav tabs ──────────────────────────────── */
      .round-nav {
        display: flex; gap: 2px; overflow-x: auto; padding: 10px 14px 0;
        scrollbar-width: none;
      }
      .round-nav::-webkit-scrollbar { display: none; }
      .round-tab {
        flex-shrink: 0; padding: 4px 11px; border: none; cursor: pointer;
        font-size: 10px; font-weight: 700; letter-spacing: .04em; text-transform: uppercase;
        background: var(--qgh-nav-bg); color: var(--qgh-color);
        transition: background .15s, color .15s;
        border-bottom: 2px solid transparent;
        border-radius: 3px 3px 0 0;
      }
      .round-tab:hover { background: var(--qgh-nav-hover); }
      .round-tab.active {
        background: transparent; color: var(--qgh-accent);
        border-bottom-color: var(--qgh-accent);
      }

      /* ── Bracket scroll container ────────────────────── */
      .bracket-wrap {
        overflow: auto;
        max-height: 460px;
        padding: 14px 14px 6px;
        scrollbar-width: thin; scrollbar-color: var(--qgh-border) transparent;
        cursor: grab;
        user-select: none;
      }
      .bracket-wrap::-webkit-scrollbar { width: 4px; height: 4px; }
      .bracket-wrap::-webkit-scrollbar-track { background: transparent; }
      .bracket-wrap::-webkit-scrollbar-thumb { background: var(--qgh-border); border-radius: 2px; }

      /* ── Bracket viewport (sized to zoomed dims for correct scroll area) ── */
      .bracket-viewport {
        position: relative;
        /* width/height set inline from JS: totalW*zoom × totalH*zoom */
      }

      /* ── Bracket canvas (natural size, scaled via transform) ────────────── */
      .bracket {
        position: absolute;
        top: 0; left: 0;
        transform-origin: top left;
        transition: transform .2s ease;
      }

      /* ── Round column ────────────────────────────────── */
      .round-col {
        flex-shrink: 0;
        position: relative;
      }
      .round-label {
        font-size: 8px; font-weight: 700; letter-spacing: .1em; text-transform: uppercase;
        text-align: center; opacity: .3; margin-bottom: 6px;
      }

      /* ── Match card ──────────────────────────────────── */
      .match-card {
        position: absolute;
        width: ${CARD_W}px;
        border: 1px solid var(--qgh-border);
        border-radius: 3px;
        overflow: hidden;
        background: var(--qgh-bg);
        transition: border-color .2s, box-shadow .2s;
      }
      .match-card:hover { border-color: color-mix(in srgb, var(--qgh-accent) 60%, transparent); }
      .match-card.live-card { border-color: var(--qgh-accent); animation: pulse-bdr 2s ease-in-out infinite; }
      @keyframes pulse-bdr {
        0%,100% { box-shadow: 0 0 0 0 transparent; }
        50%      { box-shadow: 0 0 0 3px rgba(80,200,120,.18); }
      }

      .match-team {
        display: flex; align-items: center; gap: 7px;
        padding: 0 10px;
        height: ${ROW_H}px;        /* fixed — each row is exactly ROW_H */
        box-sizing: border-box;
        font-size: 11px; position: relative;
        transition: background .15s;
      }
      .match-team + .match-team { border-top: 1px solid var(--qgh-border); }
      .match-team.winner { font-weight: 700; background: rgba(80,200,120,.09); }
      .match-team.loser  { opacity: .36; }
      .team-logo { width: 15px; height: 15px; object-fit: contain; flex-shrink: 0; }
      .team-logo-fb {
        width: 15px; height: 15px; border-radius: 50%; background: var(--qgh-fb-bg);
        display: inline-flex; align-items: center; justify-content: center;
        font-size: 7px; font-weight: 700; flex-shrink: 0;
      }
      .team-name { flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
      .team-name.tbd { opacity: .28; font-style: italic; }
      .score {
        margin-left: auto; font-size: 12px; font-weight: 700;
        font-variant-numeric: tabular-nums; flex-shrink: 0; min-width: 20px; text-align: right;
      }
      .score.live   { color: var(--qgh-accent); }
      .score.ns-time { font-size: 10px; font-weight: 400; opacity: .45; }
      .live-pip {
        position: absolute; top: 4px; right: 5px;
        width: 5px; height: 5px; border-radius: 50%; background: var(--qgh-accent);
        animation: blink 1.4s ease-in-out infinite;
      }
      @keyframes blink { 0%,100%{opacity:1} 50%{opacity:.15} }

      /* ── Connector overlay ───────────────────────────── */
      .connector-svg {
        position: absolute; top: 0; pointer-events: none; overflow: visible;
      }

      /* ── 3er Puesto section (below bracket) ─────────── */
      .third-section {
        border-top: 1px solid var(--qgh-border);
        padding: 10px 14px 12px;
      }
      .third-title {
        font-size: 8px; font-weight: 700; letter-spacing: .09em; text-transform: uppercase;
        opacity: .35; margin-bottom: 8px;
      }
      .third-card {
        border: 1px solid var(--qgh-border); border-radius: 3px; overflow: hidden;
        background: var(--qgh-bg);
      }
      .third-card .match-team { height: ${ROW_H}px; box-sizing: border-box; }
      .third-card .match-team + .match-team { border-top: 1px solid var(--qgh-border); }

      /* ── Dots navigation ─────────────────────────────── */
      .dots-nav {
        display: flex; justify-content: center; align-items: center; gap: 6px;
        padding: 10px 14px 12px;
      }
      .dot {
        height: 5px; border-radius: 3px;
        background: var(--qgh-border); cursor: pointer;
        border: none; padding: 0;
        transition: background .2s, width .25s;
        width: 5px;
      }
      .dot.active { background: var(--qgh-accent); width: 16px; }
      .dot-label {
        font-size: 9px; font-weight: 700; letter-spacing: .06em;
        color: var(--qgh-accent); opacity: .7; text-transform: uppercase;
        min-width: 56px; text-align: center;
      }

      /* ── States ──────────────────────────────────────── */
      .state-box { padding: 32px 16px; text-align: center; display: flex; flex-direction: column; align-items: center; gap: 12px; }
      .state-logo { height: 22px; width: auto; filter: var(--qgh-logo-filter); opacity: .45; }
      .state-msg { font-size: 12px; opacity: .45; }
      .sk { border-radius: 3px; background: var(--qgh-border); animation: shimmer 1.4s ease-in-out infinite; }
      @keyframes shimmer { 0%,100%{opacity:.4} 50%{opacity:.9} }
    `,
  ];

  constructor() {
    super();
    this._activeRound = 0;
    this._zoom = 1;
    this._dragStartX    = null;
    this._dragStartY    = null;
    this._dragStartLeft = null;
    this._dragStartTop  = null;
  }

  _params() {
    const p = {};
    if (this.league) p.league = this.league;
    if (this.season) p.season = this.season;
    p.timezone = this.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
    return p;
  }

  _rounds() {
    const byRound = new Map();
    for (const f of this._fixtures) {
      const round = f.league?.round || '';
      const def = ROUNDS_DEF.find(r => round.includes(r.api));
      if (!def) continue;
      if (!byRound.has(def.api)) byRound.set(def.api, { ...def, matches: [] });
      byRound.get(def.api).matches.push(f);
    }
    return ROUNDS_DEF.filter(r => byRound.has(r.api)).map(r => byRound.get(r.api));
  }

  _autoActiveRound(rounds) {
    for (let i = 0; i < rounds.length; i++) {
      if (rounds[i].matches.some(f => isLive(f.fixture.status.short))) return i;
    }
    for (let i = 0; i < rounds.length; i++) {
      if (rounds[i].matches.some(f => f.fixture.status.short === 'NS' || f.fixture.status.short === 'TBD')) return i;
    }
    return rounds.length - 1;
  }

  updated(changed) {
    super.updated?.(changed);
    if (changed.has('_fixtures') && this._fixtures.length) {
      const rounds = this._rounds();
      if (rounds.length) this._activeRound = this._autoActiveRound(rounds);
    }
  }

  _zoomIn()  { this._zoomAround(Math.min(2.0, +(this._zoom + 0.2).toFixed(1))); }
  _zoomOut() { this._zoomAround(Math.max(0.4, +(this._zoom - 0.2).toFixed(1))); }

  _onDragStart(e) {
    this._dragStartX     = e.pageX;
    this._dragStartY     = e.pageY;
    this._dragStartLeft  = e.currentTarget.scrollLeft;
    this._dragStartTop   = e.currentTarget.scrollTop;
  }
  _onDragMove(e) {
    if (e.buttons !== 1 || this._dragStartX === null) return;
    e.currentTarget.scrollLeft = this._dragStartLeft - (e.pageX - this._dragStartX);
    e.currentTarget.scrollTop  = this._dragStartTop  - (e.pageY - this._dragStartY);
  }
  _onDragEnd() { this._dragStartX = null; this._dragStartY = null; }

  _onScroll(e) {
    const wrap = e.target;
    const colW = (CARD_W + CON_W) * this._zoom;
    const newIdx = Math.min(Math.round(wrap.scrollLeft / colW), this._rounds().length - 1);
    if (newIdx !== this._activeRound) this._activeRound = newIdx;
  }

  _scrollToRound(idx) {
    const wrap = this.shadowRoot?.querySelector('.bracket-wrap');
    if (!wrap) return;
    // Scroll offset accounts for zoom since viewport div is sized at totalW*zoom
    wrap.scrollTo({ left: idx * (CARD_W + CON_W) * this._zoom, behavior: 'smooth' });
  }

  // Zoom keeping the current scroll center stable
  _zoomAround(newZoom) {
    const wrap = this.shadowRoot?.querySelector('.bracket-wrap');
    if (!wrap) {
      this._zoom = newZoom;
      return;
    }
    const centerX = wrap.scrollLeft + wrap.clientWidth / 2;
    const centerY = wrap.scrollTop  + wrap.clientHeight / 2;
    const ratio   = newZoom / this._zoom;
    this._zoom = newZoom;
    // After Lit re-renders, adjust scroll so the same point stays centered
    requestAnimationFrame(() => {
      wrap.scrollLeft = centerX * ratio - wrap.clientWidth  / 2;
      wrap.scrollTop  = centerY * ratio - wrap.clientHeight / 2;
    });
  }

  // ── Layout helpers ────────────────────────────────────────────────────────

  // Slot height doubles each round from the base (most-matches) round
  _slotH(depth) { return BASE_SLOT * Math.pow(2, depth); }

  // Y-center of match `matchIdx` at a given depth
  _yCenter(depth, matchIdx) { return matchIdx * this._slotH(depth) + this._slotH(depth) / 2; }

  // Total canvas height determined by the base round
  _totalH(baseCount) { return BASE_SLOT * baseCount; }

  // ── Match card rendering ──────────────────────────────────────────────────

  _matchCard(f, topPx) {
    const live  = isLive(f.fixture.status.short);
    const ns    = f.fixture.status.short === 'NS' || f.fixture.status.short === 'TBD';
    const ft    = !live && !ns;
    const gh    = f.goals.home;
    const ga    = f.goals.away;
    const homeW = ft && gh !== null && gh !== undefined && gh >= ga;
    const awayW = ft && ga !== null && ga !== undefined && ga > gh;

    const logo = (t) => t.logo
      ? html`<img class="team-logo" src="${t.logo}" alt="" loading="lazy">`
      : html`<span class="team-logo-fb">${(t.name || '?').charAt(0)}</span>`;

    const scoreCell = (isHome) => {
      if (ns) {
        // Show time only on away row, nothing on home row
        return isHome
          ? html``
          : html`<span class="score ns-time">${fmtTime(f.fixture.date)}</span>`;
      }
      const val = isHome ? gh : ga;
      return html`<span class="score ${live ? 'live' : ''}">${val ?? '–'}</span>`;
    };

    const row = (team, isHome) => {
      const w = isHome ? homeW : awayW;
      const l = ft && !w;
      return html`
        <div class="match-team ${w ? 'winner' : ''} ${l ? 'loser' : ''}">
          ${logo(team)}
          <span class="team-name ${!team.name ? 'tbd' : ''}">${translateCountry(team.name || '') || '—'}</span>
          ${scoreCell(isHome)}
        </div>`;
    };

    return html`
      <div class="match-card ${live ? 'live-card' : ''}" style="top:${topPx}px;left:0">
        ${live ? html`<div class="live-pip"></div>` : ''}
        ${row(f.teams.home, true)}
        ${row(f.teams.away, false)}
      </div>`;
  }

  // ── Flat match card for 3rd place section ────────────────────────────────

  _matchCardFlat(f) {
    const live  = isLive(f.fixture.status.short);
    const ns    = f.fixture.status.short === 'NS' || f.fixture.status.short === 'TBD';
    const ft    = !live && !ns;
    const gh    = f.goals.home;
    const ga    = f.goals.away;
    const homeW = ft && gh !== null && gh !== undefined && gh >= ga;
    const awayW = ft && ga !== null && ga !== undefined && ga > gh;

    const logo = (t) => t.logo
      ? html`<img class="team-logo" src="${t.logo}" alt="" loading="lazy">`
      : html`<span class="team-logo-fb">${(t.name || '?').charAt(0)}</span>`;

    const scoreCell = (isHome) => {
      if (ns) return isHome ? html`` : html`<span class="score ns-time">${fmtTime(f.fixture.date)}</span>`;
      const val = isHome ? gh : ga;
      return html`<span class="score ${live ? 'live' : ''}">${val ?? '–'}</span>`;
    };

    const row = (team, isHome) => {
      const w = isHome ? homeW : awayW;
      const l = ft && !w;
      return html`
        <div class="match-team ${w ? 'winner' : ''} ${l ? 'loser' : ''}">
          ${logo(team)}
          <span class="team-name ${!team.name ? 'tbd' : ''}">${translateCountry(team.name || '') || '—'}</span>
          ${scoreCell(isHome)}
        </div>`;
    };

    return html`${row(f.teams.home, true)}${row(f.teams.away, false)}`;
  }

  // ── Connector SVGs ───────────────────────────────────────────────────────

  // Standard convergence connector: two matches → one match in next round
  _connectorSvg(depth, nextRound, totalH) {
    const lines = [];
    const mid = CON_W / 2;
    for (let k = 0; k < nextRound.matches.length; k++) {
      const y0   = this._yCenter(depth, 2 * k);
      const y1   = this._yCenter(depth, 2 * k + 1);
      const yMid = (y0 + y1) / 2;
      lines.push(svg`
        <line x1="0"      y1="${y0}"   x2="${mid}"   y2="${y0}"   stroke="var(--qgh-border)" stroke-width="1.5"/>
        <line x1="0"      y1="${y1}"   x2="${mid}"   y2="${y1}"   stroke="var(--qgh-border)" stroke-width="1.5"/>
        <line x1="${mid}"  y1="${y0}"   x2="${mid}"   y2="${y1}"   stroke="var(--qgh-border)" stroke-width="1.5"/>
        <line x1="${mid}"  y1="${yMid}" x2="${CON_W}" y2="${yMid}" stroke="var(--qgh-border)" stroke-width="1.5"/>
      `);
    }
    return html`<svg class="connector-svg"
      style="left:${CARD_W}px;width:${CON_W}px;height:${totalH}px"
      viewBox="0 0 ${CON_W} ${totalH}"
    >${lines}</svg>`;
  }


  // ── Main render ───────────────────────────────────────────────────────────

  render() {
    if (this._loading) return html`
      <div class="widget">
        ${this.title ? html`<div class="header"><span class="header-title">${this.title}</span></div>` : ''}
        <div class="state-box">
          <div class="sk" style="width:120px;height:12px;border-radius:6px"></div>
          <div class="sk" style="width:80px;height:10px;border-radius:6px"></div>
        </div>
        ${this._poweredBy()}
      </div>`;

    if (this._error) return html`
      <div class="widget">
        ${this.title ? html`<div class="header"><span class="header-title">${this.title}</span></div>` : ''}
        <div class="state-box">
          <img class="state-logo" src="https://quienganahoy.com/svg/logo_qgh.svg" alt="">
          <span class="state-msg">No se pudo cargar</span>
        </div>
        ${this._poweredBy()}
      </div>`;

    const rounds = this._rounds();

    if (!rounds.length) return html`
      <div class="widget">
        ${this.title ? html`<div class="header"><span class="header-title">${this.title}</span></div>` : ''}
        <div class="state-box"><span class="state-msg">Sin fase eliminatoria disponible</span></div>
        ${this._poweredBy()}
      </div>`;

    const mainRounds  = rounds.filter(r => r.main);
    const thirdRound  = rounds.find(r => !r.main) || null;
    const baseCount   = mainRounds[0]?.matches.length || 1;
    const totalH      = this._totalH(baseCount);
    const activeIdx   = Math.min(this._activeRound, rounds.length - 1);
    const activeRound = rounds[activeIdx];

    const depthOf = new Map(mainRounds.map((r, i) => [r.api, i]));

    // Each column = CARD_W + connector gap, except the last
    const colW    = (ri) => CARD_W + (ri < mainRounds.length - 1 ? CON_W : 0);
    const xOfMain = (ri) => mainRounds.slice(0, ri).reduce((a, _, i) => a + colW(i), 0);
    const totalW  = mainRounds.reduce((a, _, ri) => a + colW(ri), 0);

    return html`
      <div class="widget">

        <!-- Header -->
        <div class="header">
          <span class="header-title">${this.title || 'Llaves'}</span>
          <div class="zoom-btns">
            <button class="zoom-btn" @click=${this._zoomOut} ?disabled=${this._zoom <= 0.4}>−</button>
            <button class="zoom-btn" @click=${this._zoomIn}  ?disabled=${this._zoom >= 2.0}>+</button>
          </div>
        </div>

        <!-- Round tabs -->
        <div class="round-nav">
          ${mainRounds.map((r, i) => html`
            <button
              class="round-tab ${i === activeIdx ? 'active' : ''}"
              @click=${() => { this._activeRound = i; this._scrollToRound(i); }}
            >${r.short}</button>`)}
        </div>

        <!-- Bracket canvas -->
        <div class="bracket-wrap"
          @scroll=${this._onScroll}
          @mousedown=${this._onDragStart}
          @mousemove=${this._onDragMove}
          @mouseup=${this._onDragEnd}
          @mouseleave=${this._onDragEnd}
        >
          <!-- viewport div carries the zoomed dimensions so the scrollbar is correct -->
          <div class="bracket-viewport"
            style="width:${totalW * this._zoom}px;height:${totalH * this._zoom}px"
          >
          <div class="bracket"
            style="width:${totalW}px;height:${totalH}px;transform:scale(${this._zoom})"
          >
            ${mainRounds.map((round, ri) => {
              const depth   = depthOf.get(round.api);
              const hasNext = ri < mainRounds.length - 1;

              return html`
                <div class="round-col"
                  style="position:absolute;left:${xOfMain(ri)}px;top:0;width:${colW(ri)}px;height:${totalH}px"
                >
                  ${round.matches.map((f, mi) =>
                    this._matchCard(f, this._yCenter(depth, mi) - CARD_H / 2)
                  )}
                  ${hasNext ? this._connectorSvg(depth, mainRounds[ri + 1], totalH) : ''}
                </div>`;
            })}
          </div>
          </div><!-- /bracket-viewport -->
        </div>

        <!-- 3er Puesto section -->
        ${thirdRound ? html`
          <div class="third-section">
            <div class="third-title">3er Puesto</div>
            <div class="third-card">
              ${thirdRound.matches.map(f => this._matchCardFlat(f))}
            </div>
          </div>` : ''}

        <!-- Dots nav -->
        <div class="dots-nav">
          <span class="dot-label">${activeRound.label}</span>
          ${mainRounds.map((r, i) => html`
            <button
              class="dot ${i === activeIdx ? 'active' : ''}"
              title="${r.label}"
              @click=${() => { this._activeRound = i; this._scrollToRound(i); }}
            ></button>`)}
        </div>

        ${this._poweredBy()}
      </div>`;
  }
}
